/**
 * ============================================================
 *  ORGANIZATION PAGE PALETTE  (Structure + Supply Chain)
 * ============================================================
 *
 *  Single source of truth for all design tokens used across:
 *    - StructureDashboard / SupplyChainDashboard
 *    - StructureWorkspace  / SupplyChainWorkspace
 *    - All shared components (MetricCard, OverallAnalytics,
 *      ScopeFootprint, OrganizationInsight, EmissionGrid,
 *      OrganizationLifeCycleAssessment, DocumentViewer …)
 *
 *  HOW TO USE
 *  ----------
 *  import { ORG_PALETTE, getOrgTokens } from 'path/to/organizationPalette';
 *
 *  // Inside a component that already has `isDark`:
 *  const tok = getOrgTokens(isDark);
 *  // Then use tok.border.card, tok.bg.card, tok.font.tableTitle, etc.
 * ============================================================
 */

import { GRAPH_PALETTE } from "./graphChartPalette";
import { M3_BRAND, M3_COLORS, getM3Colors, getM3Elevation } from "./m3Palette";

// Metric-card accent set for an LCA scope, derived from GRAPH_PALETTE.scopes.
// Dark-mode surfaces are the scope colour's HCT tones 10 / 20 / 30 / 80.
const scopeCard = (accent, tint, dark) => ({
  accent,
  bg: tint,
  bgDark: dark.tone10,
  iconBgDark: dark.tone20,
  iconColorDark: dark.tone80,
  borderDark: dark.tone30,
});

// ── 1. STATIC (MODE-INDEPENDENT) TOKENS ──────────────────────────────────────
export const ORG_PALETTE = {

  // ── Typography ─────────────────────────────────────────────────────────────
  font: {
    family: "Poppins, sans-serif",

    // Sizes  (px strings – use as fontSize in sx props)
    size: {
      pageTitle: "30px",   // not used in Org pages yet – reserved
      tableTitle: "20px",   // "Supply Chains" / "Organizations" heading
      chartTitle: "18px",   // "Emissions by Organization", "Scope Distribution"
      sectionTitle: "16px",   // "GHG Emission Assessment", sub-headings
      metricValue: "32px",   // large number in MetricCard (default variant)
      metricValueSm: "20px",   // large number in MetricCard (stage variant)
      metricLabel: "15px",   // title below the value in MetricCard
      metricUnit: "13px",   // unit text ("kg CO₂e", "%")
      gridHeader: "14px",   // ag-grid column header text
      gridCell: "15px",   // ag-grid cell text
      tabLabel: "14px",   // SegmentedTabs button text
      footerLabel: "14px",   // "Global View" checkbox label, pagination text
      bodyText: "14px",   // body / paragraph copy
      caption: "12px",   // small supporting labels
      badge: "12px",   // Scope 1/2/3 pill badges in EmissionGrid
    },

    // Weights
    weight: {
      light: 100,
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
  },

  // ── Spacing / Layout ───────────────────────────────────────────────────────
  spacing: {
    pageGap: 3,   // MUI spacing units – gap between major page sections (dashboard metric row → table)
    sectionGap: 3,   // gap between workspace tabs content blocks (LCA, Scope, charts)
    metricCardGap: 3,   // gap between the 4 metric cards in the grid
    tablePadH: 3,   // horizontal padding inside Table header/footer (px = spacing × 8)
    tablePadV: 2,   // vertical padding inside Table header/footer
    chartPad: 3,   // inner padding of chart Paper cards (spacing units)
    cardPad: "24px 28px",   // MetricCard default inner padding
    cardPadSm: "12px 16px",   // MetricCard stage-variant inner padding
  },

  // ── Border Radius ─────────────────────────────────────────────────────────
  radius: {
    card: "12px",   // MetricCard, chart Paper, LCA Paper, Insight Paper
    table: "12px",   // Paper "Table Container"
    tab: "12px",   // SegmentedTabs outer container
    tabItem: "10px",   // each tab pill button
    badge: "9999px", // Scope 1/2/3 pill badges
    icon: "8px",   // MetricCard icon box (default)
    iconSm: "6px",   // MetricCard icon box (stage variant)
    input: "8px",    // TextBox / SelectBox inputs
  },

  // ── Metric card accent colours (shared light & dark) ──────────────────────
  //    accent  = icon colour + strong tint
  //    bg      = card background tint (LIGHT mode)        — used when isDark=false
  //    bgDark  = card background solid (DARK mode)         — rich coloured dark surface
  metricCards: {
    // BLUE - Active Chains
    chain: {
      accent: "#38bdf8",
      bg: "rgba(14, 165, 233, 0.10)",
      bgDark: "#0A1930",
      iconBgDark: "#172A46",
      iconColorDark: "#93C5FD",
      borderDark: "#1E3A8A"
    },
    // Scope 1 / 2 / 3 follow GRAPH_PALETTE.scopes (red / orange / blue)
    scope1: scopeCard(GRAPH_PALETTE.scopes.Scope1, "rgba(239, 68, 68, 0.10)",
      { tone10: "#410004", tone20: "#68000a", tone30: "#930013", tone80: "#ffb3ad" }),
    scope2: scopeCard(GRAPH_PALETTE.scopes.Scope2, "rgba(245, 158, 11, 0.10)",
      { tone10: "#2a1700", tone20: "#472a00", tone30: "#653e00", tone80: "#ffb95f" }),
    scope3: scopeCard(GRAPH_PALETTE.scopes.Scope3, "rgba(14, 165, 233, 0.10)",
      { tone10: "#001e2f", tone20: "#00344d", tone30: "#004c6e", tone80: "#89ceff" }),
    gwp: { accent: "#fbbf24", bg: "rgba(251, 191, 36, 0.10)", bgDark: "#2A1A08", iconBgDark: "#422A12", iconColorDark: "#FDE68A", borderDark: "#78350F" },
    cost: { accent: "#4ade80", bg: "rgba(34, 197, 94, 0.10)", bgDark: "#062417", iconBgDark: "#0F3A27", iconColorDark: "#A7F3D0", borderDark: "#064E3B" },
    shield: { accent: "#a78bfa", bg: "rgba(168, 85, 247, 0.10)", bgDark: "#1E1033", iconBgDark: "#301B50", iconColorDark: "#DDD6FE", borderDark: "#4C1D95" },
  },

  // ── Scope badge pill colours ───────────────────────────────────────────────
  scopeBadge: {
    scope1: { color: GRAPH_PALETTE.scopes.Scope1, bg: "rgba(239, 68,  68, 0.10)", border: "rgba(239, 68,  68, 0.20)" },
    scope2: { color: GRAPH_PALETTE.scopes.Scope2, bg: "rgba(245,158,  11, 0.10)", border: "rgba(245,158,  11, 0.20)" },
    scope3: { color: GRAPH_PALETTE.scopes.Scope3, bg: "rgba(14, 165, 233, 0.10)", border: "rgba(14, 165, 233, 0.20)" },
    avoided: { color: M3_BRAND.primary, bg: "rgba(101,157,  51, 0.10)", border: "rgba(101,157,  51, 0.20)" },
  },

  // ── SegmentedTabs (workspace navigation) ─────────────────────────────────
  segmentedTabs: {
    containerBg: "#f1f5f9",              // outer pill background  (light)
    containerBgDk: "#27272a",              // outer pill background  (dark)
    activeColor: M3_BRAND.primary,       // active tab fill (both modes)
    activeShadow: "0 4px 12px rgba(101,157,51,0.25)",
    inactiveColor: "#475569",              // label colour (light)
    inactiveColorDk: "#94a3b8",           // label colour (dark)
    hoverBg: "#e2e8f0",             // hover (light)
    hoverBgDk: "#3f3f46",             // hover (dark)
  },

  // ── Animations ────────────────────────────────────────────────────────────
  animation: {
    tabSwitch: "org-fadeInUp 0.4s ease",
    cardHover: "translateY(-2px)",
    hoverShadow: "0 12px 30px rgba(0,0,0,0.06)",
  },
};


// ── 2. MODE-AWARE TOKEN RESOLVER ──────────────────────────────────────────────
/**
 * Call getOrgTokens(isDark) inside any component to get the full
 * set of mode-correct design tokens in one object.
 *
 * @param {boolean} isDark  – from `theme.palette.mode === 'dark'`
 * @returns {object}        – flat token map ready for sx props
 */
export const getOrgTokens = (isDark) => {
  const m3 = getM3Colors(isDark);
  const elevation = getM3Elevation(isDark);
  return {

  // ── Border ─────────────────────────────────────────────────────────────────
  border: {
    // Standard component border (card, chart Paper, LCA Paper, Insight Paper)
    card: m3.outlineVariant,
    // Table container + AgGrid header/footer divider
    table: m3.outlineVariant,
    // Table header golden border accent (GaDDi style)
    tableHeaderGold: "#e8d49b",
    // Subtle inner divider (e.g. "Emissions Breakdown" title bottom line)
    divider: m3.outlineVariant,
    // AgGrid row separator
    row: isDark ? m3.outlineVariant : m3.surfaceContainerHigh,
  },

  // ── Backgrounds ────────────────────────────────────────────────────────────
  bg: {
    // Solid card surface — lifted above page background
    card: isDark ? m3.surfaceContainerLow : m3.surfaceContainerLowest,
    // Page / outer background
    page: isDark ? m3.surfaceContainerLowest : m3.surfaceContainerLow,
    // Table header strip (clean standard header)
    tableHeader: isDark ? (m3.surfaceContainerHigh || "#1e293b") : "#f8fafc",
    // AgGrid row hover
    rowHover: m3.stateHover,
    // SegmentedTabs pill container
    tabContainer: isDark ? m3.surfaceContainerHigh : m3.surfaceContainer,
    // Active tab fill — always brand green
    tabActive: M3_BRAND.primary,
    // Metric card default fallback (use metricCards map for specific)
    metricDefault: isDark ? m3.surfaceContainer : m3.surfaceContainerLowest,
  },

  // ── Text colours ───────────────────────────────────────────────────────────
  text: {
    primary: m3.onSurface,
    secondary: m3.onSurfaceVariant,
    tableHeader: isDark ? "#94a3b8" : "#475569",
    tableTitle: m3.onSurface,
    chartTitle: m3.onSurface,
    gridHeader: isDark ? "#94a3b8" : "#475569",
    gridCell: m3.onSurface,
    muted: m3.outline,
    caption: m3.onSurfaceVariant,
    tabActive: M3_COLORS.light.onPrimary,
    tabInactive: m3.onSurfaceVariant,
    graphText: m3.onSurface,
  },

  // ── Shadows ────────────────────────────────────────────────────────────────
  shadow: {
    card: elevation[1],
    cardHover: elevation[2],
    iconBox: elevation[1],
    tabActive: ORG_PALETTE.segmentedTabs.activeShadow,
  },
  // ── Utils ───
  radius: ORG_PALETTE.radius,
  font: ORG_PALETTE.font,
  spacing: ORG_PALETTE.spacing,

  // ── Section Heading Standard (Slack + Title + Underline) ───────────────────
  sectionHeading: {
    fontFamily: ORG_PALETTE.font.family,
    fontSize: ORG_PALETTE.font.size.tableTitle, // 20px
    fontWeight: ORG_PALETTE.font.weight.medium, // 500
    color: m3.onSurface,
    barColor: M3_BRAND.primary,
    barWidth: "4px",
    paddingLeft: "1.5", // MUI spacing units
    borderBottom: `1px solid ${m3.outlineVariant}`,
    containerMb: 2,     // Margin below the header container
    titlePb: 1,        // Padding below the text (above the line)
  },

  // ── Table Title Standard (Poppins 20px) ──────────────────────────────────
  tableTitle: {
    fontFamily: ORG_PALETTE.font.family,
    fontSize: ORG_PALETTE.font.size.tableTitle, // 20px
    fontWeight: ORG_PALETTE.font.weight.medium,
    color: m3.onSurface,
    letterSpacing: '-0.02em',
  },

  // ── AgGrid Cell Text Standard ──────────────────────────────────────────────
  gridCellText: {
    fontFamily: ORG_PALETTE.font.family,
    fontSize: ORG_PALETTE.font.size.gridCell, // 15px
    fontWeight: ORG_PALETTE.font.weight.medium, // 500
    color: m3.onSurface,
  },
  };
};
